import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

const Support: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    userType: 'brand',
    priority: 'medium',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 py-20">
        <div className="mx-auto max-w-6xl text-center">
          <h1 className="mb-6 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-5xl font-bold text-transparent md:text-6xl">
            Support Center
          </h1>
          <p className="mx-auto max-w-4xl text-xl text-gray-400 md:text-2xl">
            We're here to help! Get in touch with our support team for technical assistance,
            billing questions, or general inquiries.
          </p>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Email Support */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">Email</span>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Email Support</h3>
              <p className="text-gray-400 mb-4">
                Send us an email and we'll respond within 24 hours.
              </p>
              <p className="text-lg font-semibold text-violet-400">i@kol.my</p>
              <p className="text-sm text-gray-500 mt-2">Average response: 4 hours</p>
            </div>

            {/* Live Chat */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">Chat</span>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Live Chat</h3>
              <p className="text-gray-400 mb-4">
                Chat with our support team in real-time during business hours.
              </p>
              <button className="px-6 py-3 bg-gradient-to-r from-violet-600 to-blue-500 text-white rounded-lg font-semibold hover:opacity-90 transition">
                Start Chat
              </button>
              <p className="text-sm text-gray-500 mt-2">Mon-Fri, 9am-6pm MYT</p>
            </div>

            {/* Phone Support */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">Phone</span>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Phone Support</h3>
              <p className="text-gray-400 mb-4">
                Premium support available for Enterprise customers.
              </p>
              <p className="text-lg font-semibold text-violet-400">+60 3-1234 5678</p>
              <p className="text-sm text-gray-500 mt-2">Premium customers only</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <h2 className="text-3xl font-bold mb-6 text-white text-center">Submit a Support Ticket</h2>
              
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-5xl">Check</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-2 text-white">Ticket Submitted!</h3>
                  <p className="text-gray-400 mb-6">
                    Your support ticket has been created. Our team will respond within 24 hours. 
                    You'll receive updates at {formData.email}.
                  </p>
                  <div className="bg-violet-500/10 border border-violet-500/30 p-4 rounded-lg inline-block mb-6">
                    <p className="text-sm text-gray-400">
                      <strong className="text-white">Ticket ID:</strong> #TKT-{Math.random().toString(36).substr(2, 9).toUpperCase()}
                    </p>
                  </div>
                  <br />
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="px-6 py-3 bg-gradient-to-r from-violet-600 to-blue-500 text-white rounded-lg font-semibold hover:opacity-90 transition"
                  >
                    Submit Another Ticket
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Account Type *
                      </label>
                      <select
                        name="userType"
                        required
                        value={formData.userType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-[#1a1a2e] border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white"
                        style={{ backgroundColor: '#1a1a2e' }}
                      >
                        <option value="brand" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Brand</option>
                        <option value="kol" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>KOL / Influencer</option>
                        <option value="agency" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Agency</option>
                        <option value="other" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Priority *
                      </label>
                      <select
                        name="priority"
                        required
                        value={formData.priority}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-[#1a1a2e] border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white"
                        style={{ backgroundColor: '#1a1a2e' }}
                      >
                        <option value="low" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Low - General questions</option>
                        <option value="medium" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Medium - Non-urgent issues</option>
                        <option value="high" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>High - Urgent problems</option>
                        <option value="critical" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Critical - System down</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                      placeholder="Brief description of your issue"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                      placeholder="Please describe your issue in detail..."
                    />
                  </div>

                  <div className="bg-yellow-500/10 border-l-4 border-yellow-500 p-4 rounded">
                    <p className="text-sm text-yellow-200">
                      <strong>Tip:</strong> Include your account email, any error messages you've seen, 
                      and steps to reproduce the issue for faster resolution.
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 bg-gradient-to-r from-violet-600 to-blue-500 text-white rounded-lg font-semibold text-lg hover:shadow-lg hover:shadow-violet-500/30 transition-all"
                  >
                    Submit Support Ticket
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={3} className="px-6 py-16">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="mb-6 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-4xl font-bold text-transparent">
            Frequently Asked Questions
          </h2>
          <p className="mb-8 text-xl text-gray-400">
            Get instant answers to common questions
          </p>
          <Link
            to="/faq"
            className="inline-block rounded-lg bg-gradient-to-r from-violet-600 to-blue-500 px-8 py-4 text-lg font-semibold text-white transition-all hover:shadow-xl hover:shadow-violet-500/30"
          >
            Browse FAQ
          </Link>
        </div>
      </PublicBandSection>

      <PublicBandSection band={4} className="px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-transparent">Support Hours</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-white">Email & Tickets</h3>
                <p className="text-gray-400 mb-2">Monday - Friday: 9:00 AM - 6:00 PM MYT</p>
                <p className="text-gray-400 mb-2">Saturday: 10:00 AM - 2:00 PM MYT</p>
                <p className="text-gray-400">Sunday: Closed</p>
                <p className="text-sm text-gray-500 mt-2">Response within 24 hours</p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4 text-white">Live Chat</h3>
                <p className="text-gray-400 mb-2">Monday - Friday: 9:00 AM - 6:00 PM MYT</p>
                <p className="text-gray-400 mb-2">Saturday - Sunday: Closed</p>
                <p className="text-sm text-gray-500 mt-2">Real-time support</p>
              </div>
            </div>
            <div className="mt-8 p-4 bg-violet-500/10 border border-violet-500/30 rounded-lg">
              <p className="text-center text-violet-300">
                <strong>Enterprise Customers:</strong> 24/7 priority support available. 
                Contact your account manager for access.
              </p>
            </div>
          </div>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default Support;
